import isEqual from "lodash.isequal";
import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useFormikContext } from "formik";

import {
  HvGlobalActions,
  HvTypography,
  HvGrid,
  ListValueProp,
} from "@hitachivantara/uikit-react-core";

import ConfirmationDialog from "components/common/ConfirmationDialog";
import { EventTypeDropdown } from "components/form";
import { useEventTypes, WorkflowCondition } from "lib/api";

import useStyles from "./styles";

const CLEAN_CONDITION: WorkflowCondition = {
  displayLabel: undefined,
  groupId: 0,
  groupLogic: "and",
  operator: undefined,
  property: undefined,
  type: undefined,
  value: undefined,
};

const EventType: React.FC = () => {
  const { t } = useTranslation("workflowAddEdit");
  const classes = useStyles();
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);

  const { data: eventTypeValues } = useEventTypes({
    revalidateOnFocus: false,
  });

  const { setFieldTouched, setFieldValue, setStatus, errors, touched, values } =
    useFormikContext<WorkflowFormData>();
  const [eventTypeId, setEventTypeId] = useState<string | undefined>(
    values.eventTypeId
  );

  const setValueOnForm = useCallback(
    (property: string, selection: string | number | undefined) => {
      setFieldValue(property, selection);
      // clearServerSideErrors
      setStatus({});
    },
    [setFieldValue, setStatus]
  );

  const parseStatus = (field: keyof WorkflowFormData, untouched = false) => {
    return errors[field] && (untouched || touched[field]) ? "invalid" : "valid";
  };

  const parseStatusMessage = (
    field: keyof WorkflowFormData,
    untouched = false
  ) => {
    return errors[field] && (untouched || touched[field]) ? errors[field] : "";
  };

  const onConfirmEventTypeChange = () => {
    setValueOnForm("eventTypeId", eventTypeId);
    setIsConfirmDialogOpen(false);
  };

  const onCancelEventTypeChange = () => {
    setIsConfirmDialogOpen(false);
  };

  const isInInitialState = (conditions: WorkflowCondition[]) =>
    conditions.length === 0 ||
    (conditions.length === 1 && isEqual(conditions[0], CLEAN_CONDITION));

  return (
    <HvGrid container className={classes.root} id="event-type">
      <HvGrid sm={12} item>
        <HvGlobalActions title={t("eventType.title")} variant="section" />
        <HvTypography variant="normalText">
          {t("eventType.description")}
        </HvTypography>
      </HvGrid>
      <HvGrid container item xs={12} lg={9}>
        <HvGrid item xs={12} md={6}>
          <ConfirmationDialog
            isOpen={isConfirmDialogOpen}
            onConfirm={onConfirmEventTypeChange}
            onCancel={onCancelEventTypeChange}
            title={t("eventType.changeDialog.title")}
            message={t("eventType.changeDialog.body")}
            confirmButtonLabel={t("eventType.changeDialog.confirmButtonLabel")}
          />
          <EventTypeDropdown
            className={classes.input}
            name="eventTypeId"
            selectedEventType={values.eventTypeId}
            events={eventTypeValues?.data}
            status={parseStatus("eventTypeId")}
            statusMessage={parseStatusMessage("eventTypeId")}
            label={t("eventType.label")}
            placeholder={t("eventType.eventPlaceholder")}
            required
            onChange={(selection) => {
              const { id } = selection as ListValueProp;
              if (values.conditions && !isInInitialState(values.conditions)) {
                setEventTypeId(id);
                setIsConfirmDialogOpen(true);
              } else {
                setValueOnForm("eventTypeId", id);
              }
            }}
            onToggle={() => {
              setFieldTouched("eventTypeId", true, false);
            }}
          />
        </HvGrid>
      </HvGrid>
    </HvGrid>
  );
};

export default EventType;
